import { faRefresh } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain } from '@feathr/blackbox';
import {
  domainStatusToColorMap,
  domainStatusToLabelMap,
  EPinpointRequestStatus,
} from '@feathr/blackbox';
import { Button, Fieldset, Form, Pill, Spinner, Tooltip, Value } from '@feathr/components';
import { ContentStatusPill, EmailStatusPill } from '@feathr/extender/components/DomainStatusPill';
import { useRefreshDomain } from '@feathr/extender/hooks';
import { useFlags } from '@feathr/extender/state';

import * as styles from './DomainDetails.css';

interface IProps {
  domain: Domain;
}

async function poll(domain: Domain, setTimeout: (timeout: number) => void): Promise<void> {
  await domain.reload();
  if (!domain.isErrored) {
    setTimeout(window.setTimeout(poll, 1000, domain, setTimeout));
  }
}

function DomainDetails({ domain }: IProps): JSX.Element {
  const flags = useFlags();
  const { t } = useTranslation();
  const refreshDomain = useRefreshDomain(domain);
  const [timeout, setTimeout] = useState<number | undefined>();

  useEffect(() => {
    if (timeout === undefined) {
      void poll(domain, setTimeout);
    }
    return () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, [domain, timeout, setTimeout]);

  if (domain.isPending) {
    return <Spinner />;
  }

  const emailStatus = domain.emailSendingStatus(flags.bypassMX);

  function handleClickRefresh(): void {
    void refreshDomain();
  }

  return (
    <Form label={t('Domain information')}>
      <Fieldset direction={'column'}>
        <Value
          className={styles.status}
          label={t('Content serving status')}
          value={<ContentStatusPill domain={domain} />}
        />
        <Value
          className={styles.status}
          label={t('Email sending status')}
          value={
            <div className={styles.emailStatusContainer}>
              <EmailStatusPill domain={domain} />
              {emailStatus === EPinpointRequestStatus.Failed && (
                <Tooltip
                  title={t(
                    'Restart the email domain verification process. This may take up to 24 hours to complete.',
                  )}
                >
                  <Button className={styles.refresh} onClick={handleClickRefresh} type={'link'}>
                    {t('Refresh')} <FontAwesomeIcon icon={faRefresh} size={'lg'} />
                  </Button>
                </Tooltip>
              )}
            </div>
          }
        />
        <Value
          className={styles.status}
          label={t('DKIM status')}
          value={
            <Pill
              isLoading={domain.isPending}
              theme={domainStatusToColorMap[domain.get('dkim')?.status || 'N/A']}
            >
              {domainStatusToLabelMap[domain.get('dkim')?.status || 'N/A']}
            </Pill>
          }
        />
        <Value
          className={styles.status}
          label={t('MX status')}
          value={
            <Pill
              isLoading={domain.isPending}
              theme={domainStatusToColorMap[domain.get('mx')?.status || 'N/A']}
            >
              {domainStatusToLabelMap[domain.get('mx')?.status || 'N/A']}
            </Pill>
          }
        />
      </Fieldset>
    </Form>
  );
}

export default observer(DomainDetails);
